import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Button } from '@wf/components';

import { downloadAttachment } from '../../../../../services/attachmentService';

import { attachmentFileUrlSelector } from '../../../store/selectors';
import { downloadFile } from '../../../../../helpers';
import { type RootState } from '../../../../../store';

import styles from './FilePreview.module.scss';

interface IFilePreviewProps {
  fileURL: string;
  fileName: string;
  index: number;
  onReplaceFile: () => void;
  viewMode: boolean;
  id?: number;
  parentAttachmentId?: number;
}

const fileUrlCache: Record<string, string> = {};

const blobCache: Record<string, Blob> = {};

function FilePreview({ fileURL, fileName, index, onReplaceFile, viewMode, id, parentAttachmentId }: IFilePreviewProps) {
  const { t } = useTranslation();
  const { id: lpcoId } = useParams();
  const initialAttachmentFileUrl = useSelector((state: RootState) => attachmentFileUrlSelector(state, id));

  const [url, setUrl] = useState(fileUrlCache[fileURL] || '');
  const [blob, setBlob] = useState<Blob | null>(blobCache[fileURL] || null);

  useEffect(() => {
    if (url) {
      return;
    }

    const isSaved = !!parentAttachmentId || initialAttachmentFileUrl === fileURL;

    if (!isSaved && initialAttachmentFileUrl) {
      delete fileUrlCache[initialAttachmentFileUrl];
      delete blobCache[initialAttachmentFileUrl];
    }

    async function getUrl() {
      const { data } = await downloadAttachment({
        fileURL,
        lpcoId,
        attachmentId: parentAttachmentId ?? id,
        isSaved,
      });
      setBlob(data);
      blobCache[fileURL] = data;

      const newUrl = URL.createObjectURL(data);
      setUrl(newUrl);
      fileUrlCache[fileURL] = newUrl;
    }

    getUrl().catch((e) => console.error(e));
  }, [fileURL, lpcoId, url, id, initialAttachmentFileUrl]);

  const onDownload = () => {
    downloadFile(blob as Blob, fileName || `Document-${index + 1}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>{t('Attached File')}</span>
        <div>
          {!viewMode && (
            <Button onClick={onReplaceFile} ghost>
              {t('Replace File')}
            </Button>
          )}
          <Button onClick={onDownload} disabled={!blob} ghost>
            {t('Download')}
          </Button>
        </div>
      </div>
      {!url ? (
        <div>{t('Loading...')}</div>
      ) : fileURL.endsWith('pdf') ? (
        <iframe src={url} title="pdf-preview" className={styles.iframe} />
      ) : (
        <img src={url} alt="" className={styles.image} />
      )}
    </div>
  );
}

export default FilePreview;
